import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface DetailOverlayProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * Full-area detail overlay: a centered card that covers the viewport with a
 * dimmed backdrop and a close (✕) button. Used for double-click "open full
 * details" on tables that have no dedicated route (e.g. active sessions).
 * Escape or a backdrop click closes it.
 */
export function DetailOverlay({ open, onClose, title, children, className }: DetailOverlayProps) {
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/40 p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        className={cn(
          "flex w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-xl",
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h3 className="text-lg font-semibold truncate">{title || ""}</h3>
          <Button variant="ghost" size="icon-sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
